import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import {
  useUpsells,
  useCreateUpsellContainer,
  useSoftDeleteUpsell,
  useApproveUpsell,
  type UpsellWithLines,
} from "@/hooks/use-upsells";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useProjectDetail } from "@/hooks/use-project-detail";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { UpsellEditDialog } from "./UpsellEditDialog";
import { UpsellImportFromPdfDialog } from "./UpsellImportFromPdfDialog";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import { computeTMInvoicedTotal, computeUpsellTotals } from "@/lib/upsell-totals";
import { MersalgTable, type MersalgRowData } from "./mersalg/MersalgTable";

export default function MersalgTab({
  projectId,
  readOnly = false,
}: {
  projectId: string;
  readOnly?: boolean;
}) {
  const { data: upsells = [], isLoading } = useUpsells(projectId);
  const { data: projectDetail } = useProjectDetail(projectId);
  const { activeUser } = useImpersonation();
  // VIB-722c: manage_upsells gates CRUD-knapper (opprett, godkjenn, slett).
  // Resolver inkluderer admin/superadmin-override og prosjekt-rolle-matrise
  // (default: project_lead + client_advisor=true, sales=false, assignee=false).
  const canManageUpsells = useProjectPermission(projectId, "manage_upsells");
  const createContainer = useCreateUpsellContainer();
  const softDeleteMut = useSoftDeleteUpsell();
  const approveMut = useApproveUpsell();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const editingUpsell = upsells.find((u) => u.id === editingId) ?? null;
  const deletingUpsell = upsells.find((u) => u.id === deletingId) ?? null;

  const canCrud = !readOnly && canManageUpsells;
  const canApprove = canCrud;

  const defaultOverheadPct = projectDetail?.offerMetadata?.overhead_pct ?? 0;
  const invoices = projectDetail?.invoices ?? [];

  async function handleCreate() {
    const id = await createContainer.mutateAsync({ project_id: projectId });
    setEditingId(id);
  }

  const drafts = upsells.filter((u) => u.status === "draft");
  const approved = upsells.filter((u) => u.status === "approved");

  // VIB-994: T&M-mersalg har ingen fastsum (amount_excl_vat holdes alltid 0)
  // — hold dem ute av fastpris-summeringen, ellers ville et PL-fylt
  // time-/materiell-ESTIMAT på linjene (kun til styring) telle som ekte sum.
  const fixedApproved = approved.filter((u) => u.billing_mode !== "tm");
  const sumApproved = fixedApproved.reduce(
    (s, u) =>
      s +
      computeUpsellTotals({
        lines: u.lines,
        overhead_pct: u.overhead_pct,
        discount_pct: u.discount_pct,
        discount_amount: u.discount_amount,
        defaultOverheadPct,
      }).total,
    0,
  );
  const marginApproved = fixedApproved.reduce(
    (s, u) =>
      s +
      computeUpsellTotals({
        lines: u.lines,
        overhead_pct: u.overhead_pct,
        discount_pct: u.discount_pct,
        discount_amount: u.discount_amount,
        defaultOverheadPct,
      }).totalMargin,
    0,
  );

  function buildRows(rows: UpsellWithLines[]): MersalgRowData[] {
    return rows.map((u) => {
      const isTM = u.billing_mode === "tm";
      const { total, totalMargin: margin } = computeUpsellTotals({
        lines: u.lines,
        overhead_pct: u.overhead_pct,
        discount_pct: u.discount_pct,
        discount_amount: u.discount_amount,
        defaultOverheadPct,
      });
      return {
        upsell: u,
        isTM,
        margin,
        total,
        tmInvoicedTotal: isTM ? computeTMInvoicedTotal(u.id, invoices) : 0,
      };
    });
  }

  function renderTable(rows: UpsellWithLines[], variant: "draft" | "approved") {
    return (
      <MersalgTable
        rows={buildRows(rows)}
        variant={variant}
        canCrud={canCrud}
        canApprove={canApprove}
        activeUserId={activeUser?.id}
        isApproving={approveMut.isPending}
        onApprove={(u) => {
          if (!activeUser?.id) return;
          approveMut.mutate({ id: u.id, project_id: projectId, approved_by: activeUser.id });
        }}
        onEdit={(u) => setEditingId(u.id)}
        onDelete={(u) => setDeletingId(u.id)}
      />
    );
  }

  return (
    <>
      <CollapsibleCard
        title="Mersalg / Ekstra"
        icon={<SrIcon name="shopping-bag" className="w-5 h-5 text-primary" />}
        count={approved.length > 0 ? approved.length : undefined}
        headerExtra={
          <>
            {drafts.length > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                {drafts.length} utkast
              </span>
            )}
            {sumApproved > 0 && (
              <span className="text-sm font-medium text-muted-foreground ml-2">
                {formatNOK(sumApproved)}
              </span>
            )}
          </>
        }
        actions={(open) =>
          canCrud && open ? (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => setImportOpen(true)}
              >
                <SrIcon name="upload" className="w-3 h-3 mr-1" />
                Nytt mersalg fra PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={handleCreate}
                disabled={createContainer.isPending}
              >
                {createContainer.isPending ? (
                  <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <SrIcon name="plus" className="w-3 h-3 mr-1" />
                )}
                Nytt mersalg
              </Button>
            </>
          ) : null
        }
        contentClassName="border-t"
      >
        {isLoading ? (
          <div className="flex justify-center py-4">
            <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : upsells.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Ingen mersalg registrert.</p>
            <p className="text-xs text-muted-foreground">
              Hvert mersalg blir én fakturalinje og kan inneholde flere oppgaver/tjenester.
            </p>
          </div>
        ) : (
          <>
            {drafts.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-amber-50/50 border-b text-xs uppercase tracking-wider text-amber-800 font-medium flex items-center gap-2">
                  <SrIcon name="clock" className="w-3 h-3" />
                  Utkast — venter på godkjenning ({drafts.length})
                </div>
                {renderTable(drafts, "draft")}
              </div>
            )}

            {approved.length > 0 && (
              <div>
                {drafts.length > 0 && (
                  <div className="px-4 py-2 bg-muted/30 border-b text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    Godkjent ({approved.length})
                  </div>
                )}
                {renderTable(approved, "approved")}
              </div>
            )}

            <div className="flex items-center justify-between px-4 py-3 border-t text-xs text-muted-foreground">
              <span>
                Godkjent {approved.length} mersalg ·{" "}
                <strong className="text-foreground">{formatNOK(sumApproved)}</strong>
              </span>
              <span>
                Margin:{" "}
                <strong className={cn(marginApproved >= 0 ? "text-success" : "text-destructive")}>
                  {formatNOK(marginApproved)}
                </strong>
              </span>
            </div>
          </>
        )}
      </CollapsibleCard>

      {editingUpsell && (
        <UpsellEditDialog
          projectId={projectId}
          upsell={editingUpsell}
          open={editingId !== null}
          onOpenChange={(o) => {
            if (!o) setEditingId(null);
          }}
          readOnly={readOnly}
          defaultOverheadPct={defaultOverheadPct}
        />
      )}

      <UpsellImportFromPdfDialog
        projectId={projectId}
        open={importOpen}
        onOpenChange={setImportOpen}
      />

      <AlertDialog
        open={deletingId !== null}
        onOpenChange={(o) => {
          if (!o) setDeletingId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett mersalg?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingUpsell && (
                <>
                  "{deletingUpsell.description}" med {deletingUpsell.lines.length}{" "}
                  linje(r) blir merket som slettet. Du kan se historikken i
                  databasen, men det vises ikke lenger i appen og teller ikke i GEEK.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingId) {
                  softDeleteMut.mutate({ id: deletingId, project_id: projectId });
                  setDeletingId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
